import React, { useState } from 'react';

import { MIN_PASSWORD_LENGTH, ROUTES } from '@elara/client-core/constants';
import { formatPasswordRequirementsError } from '@elara/client-core/lib/auth-error-utils';

import { useCurrentUserProfile, useRouterState, useSessionState, useToastState } from '#states/hooks.js';
import { Breadcrumbs } from '#ui/Breadcrumbs.js';
import {
  AppleIcon,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FloatingInput,
  GoogleIcon,
} from '#ui/index.js';
import { AppLayout, AppLayoutTitle } from '#views/layout/AppLayout.jsx';

const TITLE_CLASSES = 'text-xl font-regular';

function hasPasswordProvider(user: { providerData: Array<{ providerId: string }> }): boolean {
  return user.providerData.some((p) => p.providerId === 'password');
}

type ProviderIcon = React.FC<{ className?: string }>;

/** Federated sign-in providers we render a branded label + glyph for on the security page. */
const SOCIAL_PROVIDERS: Record<string, { label: string; Icon: ProviderIcon }> = {
  'google.com': { label: 'Google', Icon: GoogleIcon },
  'apple.com': { label: 'Apple', Icon: AppleIcon },
};

/**
 * Resolves which social provider the user signed in with from their Firebase `providerData`, so the
 * card shows the real provider (Google or Apple) rather than a hardcoded one. Returns `undefined` for
 * an unrecognised provider, which the UI renders with a generic "connected account" copy.
 */
export function getSocialProvider(user: { providerData: Array<{ providerId: string }> }):
  | { label: string; Icon: ProviderIcon }
  | undefined {
  for (const p of user.providerData) {
    const match = SOCIAL_PROVIDERS[p.providerId];
    if (match) return match;
  }
  return undefined;
}

export function SecurityPage() {
  const sessionState = useSessionState();
  const routerState = useRouterState();
  const toastState = useToastState();
  const profile = useCurrentUserProfile();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | undefined>();

  const user = sessionState.user;

  if (!user) {
    return null;
  }

  const displayName = profile?.displayName || '';
  const initials = profile?.initials || '';

  const isPasswordUser = hasPasswordProvider(user);
  const socialProvider = getSocialProvider(user);
  const providerLabel = socialProvider?.label;
  const ProviderIcon = socialProvider?.Icon;

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(undefined);

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setFormError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError('New password and confirmation do not match');
      return;
    }

    setIsSubmitting(true);
    try {
      await sessionState.updatePassword(newPassword);
      toastState.addToast({ title: 'Password updated', variant: 'success' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: unknown) {
      const err = error as { code?: string };
      if (err.code === 'auth/requires-recent-login') {
        if (!currentPassword.trim()) {
          setFormError('Please enter your current password to continue');
          setIsSubmitting(false);
          return;
        }
        try {
          await sessionState.reauthenticateWithEmailAndPassword(user.email ?? '', currentPassword);
          await sessionState.updatePassword(newPassword);
          toastState.addToast({ title: 'Password updated', variant: 'success' });
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        } catch (reauthError: unknown) {
          const friendly = formatPasswordRequirementsError(reauthError);
          const reauthErr = reauthError as Error;
          setFormError(friendly ?? reauthErr.message ?? 'Failed to update password');
        }
      } else {
        const friendly = formatPasswordRequirementsError(error);
        setFormError(friendly ?? (error as Error).message ?? 'Failed to update password');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppLayout
      backButtonRoute={ROUTES.ACCOUNT}
      title={<AppLayoutTitle title="Security" />}
      transitionKey={routerState.currentRoute?.path}
    >
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto">
          <div className="max-w-2xl mx-auto w-full px-4 pt-12 pb-16 flex flex-col gap-6">
            <div className="flex flex-col gap-3 mb-2">
              <Breadcrumbs items={[{ label: 'Account', to: ROUTES.ACCOUNT }, { label: 'Security' }]} className="mb-3" />
              <h1 className="text-2xl xl:text-3xl font-display font-light">Security</h1>
              <p className="text-muted-foreground">Manage your account security and sign-in options.</p>
            </div>

            {isPasswordUser ? (
              <Card className="shadow-sm p-6 dark:bg-background-secondary">
                <CardHeader>
                  <CardTitle className={TITLE_CLASSES}>Change password</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleChangePassword} className="space-y-6">
                    {formError && (
                      <div className="bg-destructive/10 text-destructive px-4 py-2 rounded-md text-sm whitespace-pre-line">
                        {formError}
                      </div>
                    )}
                    <FloatingInput
                      id="current-password"
                      label="Current password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      disabled={isSubmitting}
                      autoComplete="current-password"
                    />
                    <hr />
                    <div className="space-y-2">
                      <FloatingInput
                        id="new-password"
                        label="New password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={isSubmitting}
                        minLength={MIN_PASSWORD_LENGTH}
                        autoComplete="new-password"
                      />
                      <p className="text-xs text-muted-foreground mt-2">At least {MIN_PASSWORD_LENGTH} characters.</p>
                    </div>
                    <FloatingInput
                      id="confirm-password"
                      label="Confirm new password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isSubmitting}
                      minLength={MIN_PASSWORD_LENGTH}
                      autoComplete="new-password"
                    />
                    <Button type="submit" disabled={isSubmitting}>
                      Update password
                    </Button>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <Card className="shadow-sm p-6 dark:bg-background-secondary">
                <CardHeader>
                  <CardTitle className={`${TITLE_CLASSES} flex items-center gap-2`}>
                    {ProviderIcon && <ProviderIcon className="h-5 w-5" />}
                    {providerLabel ? `${providerLabel} account connected` : 'Connected account'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 px-4 py-3 rounded-md border">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={user.photoURL || undefined} alt={displayName} />
                        <AvatarFallback>{initials}</AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <div className="font-medium">{displayName}</div>
                        <div className="text-sm">{user.email}</div>
                      </div>
                    </div>
                    <p className="text-sm">
                      {providerLabel
                        ? `You sign in with ${providerLabel} and do not have a password.`
                        : 'You sign in with a connected account and do not have a password.'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
